import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin, Medal, Share2 } from "lucide-react";
import ShareEventCard from "@/components/ShareEventCard";

/**
 * Homepage spotlight for the flagship upcoming event (e.g. LATENT 5K).
 * Content is fully admin-editable in Admin → Settings → Featured Event.
 */
export default function FeaturedEvent({ event }) {
  if (!event || !event.enabled || !event.title) return null;

  const eventDate = parseDate(event.date);
  const isPast = eventDate && eventDate.getTime() < Date.now() - 12 * 60 * 60 * 1000;
  if (isPast) return null;

  const distances = (event.distances || []).filter(Boolean);
  const highlights = (event.highlights || []).filter(Boolean);
  const external = /^https?:\/\//i.test(event.register_url || "");
  const ctaLabel = external ? "REGISTER NOW" : "JOIN & REGISTER";

  const Cta = external ? (
    <a
      href={event.register_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="featured-event-register-btn"
      className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-display tracking-wider text-base px-7 py-4 transition group"
    >
      {ctaLabel} <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
    </a>
  ) : (
    <Link
      to="/register"
      data-testid="featured-event-register-btn"
      className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-display tracking-wider text-base px-7 py-4 transition group"
    >
      {ctaLabel} <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
    </Link>
  );

  return (
    <section
      data-testid="featured-event-section"
      className="relative border-y border-orange-600/40 bg-zinc-950 overflow-hidden grain"
    >
      {/* subtle orange wash */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top_left,rgba(234,88,12,0.18),transparent_55%)]" aria-hidden="true" />
      <div className="absolute -right-10 -bottom-14 font-display text-[18rem] leading-none text-white/[0.025] select-none pointer-events-none hidden lg:block" aria-hidden="true">
        {distances[0]?.replace(/\s.*$/, "") || "5K"}
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* POSTER */}
          <div className="lg:col-span-5 order-1">
            <div className="relative aspect-[4/5] hairline border-zinc-800 bg-zinc-900 overflow-hidden">
              {event.poster_image ? (
                <img
                  src={event.poster_image}
                  alt={`${event.title} poster`}
                  data-testid="featured-event-poster"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <PosterFallback title={event.title} distances={distances} date={eventDate} />
              )}
              <div className="absolute top-4 left-4 bg-orange-600 px-3 py-1.5">
                <p className="font-mono text-[10px] tracking-[0.3em] text-white uppercase flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  {eventDate ? "REGISTRATIONS OPEN" : "COMING SOON"}
                </p>
              </div>
            </div>
          </div>

          {/* DETAILS */}
          <div className="lg:col-span-7 order-2">
            <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase" data-testid="featured-event-eyebrow">
              {event.eyebrow || "NEXT UP · FLAGSHIP EVENT"}
            </p>
            <h2 className="font-editorial text-6xl sm:text-7xl lg:text-8xl text-white leading-[0.9] mt-3" data-testid="featured-event-title">
              {event.title}
            </h2>
            {event.tagline && (
              <p className="font-display text-xl sm:text-2xl text-orange-500 tracking-wide mt-3" data-testid="featured-event-tagline">
                {event.tagline}
              </p>
            )}
            {event.description && (
              <p className="text-zinc-300 text-base sm:text-lg max-w-2xl mt-5 leading-relaxed" data-testid="featured-event-description">
                {event.description}
              </p>
            )}

            {/* Meta row */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-800 hairline border-zinc-800" data-testid="featured-event-meta">
              <Meta icon={CalendarDays} label="DATE" value={eventDate ? formatDate(eventDate) : "TO BE ANNOUNCED"} />
              <Meta icon={Clock} label="FLAG OFF" value={event.time || "TBA"} />
              <Meta icon={MapPin} label="VENUE" value={event.location || "Odisha"} />
            </div>

            {/* Distances + fee */}
            {(distances.length > 0 || event.entry_fee) && (
              <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="featured-event-distances">
                {distances.map((d) => (
                  <span key={d} className="font-display text-sm tracking-[0.2em] px-3 py-1.5 hairline border-orange-600/60 text-orange-500">
                    {d}
                  </span>
                ))}
                {event.entry_fee && (
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-2 bg-zinc-900 text-zinc-300">
                    ENTRY · {event.entry_fee}
                  </span>
                )}
              </div>
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <ul className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl" data-testid="featured-event-highlights">
                {highlights.slice(0, 4).map((h) => (
                  <li key={h} className="flex items-start gap-2">
                    <Medal size={14} className="text-orange-500 shrink-0 mt-0.5" />
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-400 leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Countdown + CTA */}
            <div className="mt-10 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-10">
              {eventDate && <Countdown target={eventDate} />}
              <div className="flex flex-wrap gap-3">
                {Cta}
                <Link
                  to="/events"
                  data-testid="featured-event-details-link"
                  className="inline-flex items-center border border-zinc-700 text-white hover:bg-zinc-900 font-display tracking-wider text-base px-6 py-4 transition"
                >
                  ALL EVENTS
                </Link>
                <ShareEventCard event={event}>
                  <button
                    type="button"
                    data-testid="featured-event-share-btn"
                    className="inline-flex items-center gap-2 border border-orange-600/60 text-orange-500 hover:bg-orange-600 hover:text-white font-display tracking-wider text-base px-6 py-4 transition"
                  >
                    <Share2 size={16} /> I&apos;M RUNNING — SHARE
                  </button>
                </ShareEventCard>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="bg-zinc-950 px-5 py-4">
      <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase flex items-center gap-1.5">
        <Icon size={11} className="text-orange-500" /> {label}
      </p>
      <p className="font-display text-base sm:text-lg text-white tracking-wide mt-1.5 leading-tight">{value}</p>
    </div>
  );
}

function PosterFallback({ title, distances, date }) {
  return (
    <div className="w-full h-full hero-bg flex flex-col justify-between p-7 relative">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(234,88,12,0.35),transparent_60%)]" aria-hidden="true" />
      <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-300 uppercase relative mt-10">ODISHA STEEL CITY RUNNERS</p>
      <div className="relative">
        <p className="font-editorial text-6xl sm:text-7xl text-white leading-[0.85] break-words">{title}</p>
        <div className="mt-4 h-0.5 w-16 bg-orange-500" />
        <p className="font-display text-sm tracking-[0.25em] text-orange-400 mt-4">
          {(distances || []).join("  ·  ") || "COMMUNITY RUN"}
        </p>
        <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-400 uppercase mt-2">
          {date ? formatDate(date) : "DATE TO BE ANNOUNCED"}
        </p>
      </div>
    </div>
  );
}

function Countdown({ target }) {
  const [left, setLeft] = useState(() => diff(target));
  useEffect(() => {
    const id = setInterval(() => setLeft(diff(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!left) return null;
  const cells = [
    ["DAYS", left.d],
    ["HRS", left.h],
    ["MIN", left.m],
    ["SEC", left.s],
  ];
  return (
    <div data-testid="featured-event-countdown">
      <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-2">STARTS IN</p>
      <div className="flex gap-3">
        {cells.map(([k, v]) => (
          <div key={k} className="text-center min-w-[3.25rem]">
            <div className="font-display text-3xl sm:text-4xl text-white leading-none tabular-nums">{String(v).padStart(2, "0")}</div>
            <div className="font-mono text-[9px] tracking-[0.25em] text-orange-500 mt-1">{k}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function diff(target) {
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

function parseDate(str) {
  if (!str) return null;
  // Treat YYYY-MM-DD as 5:30 AM IST on that day (typical flag-off), so the
  // countdown and "past" check behave sensibly for Indian visitors.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str.trim());
  const d = m ? new Date(`${str}T05:30:00+05:30`) : new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d) {
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }).toUpperCase();
}
